import { Link } from "react-router-dom";
import { ShieldHalf, Building2, ArrowUpRight } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const AUDIENCES = [
  {
    icon: ShieldHalf,
    tone: "orange",
    label: "Für Vereine",
    title: "Sichtbar werden und gezielt suchen.",
    text: "Ein Profil, über das euch Unternehmen finden, eine Filtersuche für den umgekehrten Weg - und Werkzeuge, die euch die Arbeit drumherum abnehmen.",
    points: [
      "Vereinsprofil mit Reichweite und freien Flächen",
      "Projekte mit Ziel, Budget und Gegenleistung",
      "Pitch-Generator und Media-Kit-Templates",
      "Auswertung als Argument für die Verlängerung",
    ],
    to: "/fuer-vereine",
  },
  {
    icon: Building2,
    tone: "blue",
    label: "Für Unternehmen",
    title: "Entscheiden mit Zahlen statt Bauchgefühl.",
    text: "Passende Vereine und Projekte nach klaren Kriterien finden - und über die gesamte Laufzeit sehen, was das Engagement bewirkt.",
    points: [
      "Filtersuche über Region, Sportart, Zielgruppe, Reichweite, Projektart",
      "Matching als nachvollziehbarer MatchScore",
      "Analytics-Dashboard zu jedem Sponsoring",
      "Bericht, den man intern vorlegen kann",
    ],
    to: "/fuer-unternehmen",
  },
];

export const Audiences = () => {
  return (
    <section
      id="zielgruppen"
      data-testid="audiences-section"
      className="relative py-24 lg:py-32"
    >
      <div className="pointer-events-none absolute inset-0 overflow-hidden -z-10">
        <div className="absolute bottom-0 right-0 w-[600px] h-[420px] rounded-full bg-brand-orange/10 blur-[170px]" />
      </div>

      <div className="px-6 md:px-10 lg:px-16">
        <Reveal className="max-w-2xl mb-14">
          <p className="text-xs md:text-sm tracking-[0.25em] uppercase font-bold text-brand-orange mb-5">
            Für wen
          </p>
          <h2 className="font-display font-bold tracking-tight text-4xl md:text-5xl lg:text-6xl leading-[0.98]">
            Eine Plattform,{" "}
            <span className="gradient-text">zwei Blickwinkel</span>.
          </h2>
          <p className="mt-6 text-base md:text-lg leading-relaxed text-white/55">
            Vereine und Unternehmen nutzen dieselbe Plattform, brauchen aber
            Unterschiedliches, um am Ende dasselbe zu haben: eine Partnerschaft,
            die sich für beide rechnet.
          </p>
        </Reveal>

        <div className="grid gap-5 md:gap-6 lg:grid-cols-2">
          {AUDIENCES.map((a, i) => {
            const Icon = a.icon;
            return (
              <Reveal key={a.to} delay={i * 0.08}>
                <Link
                  to={a.to}
                  data-testid={`audience-card-${a.tone}`}
                  className="group relative flex h-full flex-col overflow-hidden rounded-3xl border border-white/10 backdrop-blur-xl p-8 md:p-10 hover:border-white/25 transition-colors duration-500"
                >
                  <div
                    className={`pointer-events-none absolute -top-24 ${
                      a.tone === "orange" ? "-left-16" : "-right-16"
                    } w-72 h-72 rounded-full blur-[110px] opacity-60 group-hover:opacity-100 transition-opacity duration-700 ${
                      a.tone === "orange"
                        ? "bg-brand-orange/20"
                        : "bg-brand-blue/20"
                    }`}
                  />

                  <div className="relative flex items-center gap-3 mb-7">
                    <span className="flex items-center justify-center w-12 h-12 rounded-xl bg-white/[0.06] border border-white/10">
                      <Icon
                        className={`w-5 h-5 ${
                          a.tone === "orange"
                            ? "text-brand-orange"
                            : "text-brand-blue"
                        }`}
                      />
                    </span>
                    <span className="text-xs uppercase tracking-[0.2em] font-bold text-white/45">
                      {a.label}
                    </span>
                  </div>

                  <h3 className="relative font-display font-bold text-2xl md:text-3xl text-white mb-3">
                    {a.title}
                  </h3>
                  <p className="relative text-base leading-relaxed text-white/55">
                    {a.text}
                  </p>

                  <ul className="relative mt-7 pt-7 border-t border-white/10 space-y-3 flex-1">
                    {a.points.map((p) => (
                      <li key={p} className="flex items-start gap-3">
                        <span className="mt-2 w-1.5 h-1.5 shrink-0 rounded-full bg-gradient-to-r from-brand-orange to-brand-blue" />
                        <span className="text-sm leading-relaxed text-white/70">
                          {p}
                        </span>
                      </li>
                    ))}
                  </ul>

                  <span className="relative mt-8 inline-flex items-center gap-2 text-sm font-bold text-white">
                    {a.label} ansehen
                    <ArrowUpRight className="w-4 h-4 group-hover:rotate-45 transition-transform duration-300" />
                  </span>

                  <div className="absolute bottom-0 left-0 h-1 w-0 bg-gradient-to-r from-brand-orange to-brand-blue group-hover:w-full transition-all duration-700" />
                </Link>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};
